import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { matchesAll } from "@/lib/trFold";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ScrollText, Plus, Loader2, Search, Filter, Trash2, Pencil,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("tr-TR"); } catch { return "—"; } };

const STATUS_STYLES = {
  portfoyde:   { label: "Portföyde",   cls: "bg-blue-500/15 text-blue-500 border-blue-500/40" },
  bankada:     { label: "Bankada",     cls: "bg-amber-500/15 text-amber-500 border-amber-500/40" },
  tahsil:      { label: "Tahsil",      cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40" },
  karsiliksiz: { label: "Karşılıksız", cls: "bg-red-500/15 text-red-500 border-red-500/40" },
  iade:        { label: "İade",        cls: "bg-slate-500/15 text-slate-500 border-slate-500/40" },
  ciro:        { label: "Ciro",        cls: "bg-purple-500/15 text-purple-500 border-purple-500/40" },
};

export default function Checks() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/finance/checks"),
        api.get("/customers"),
      ]);
      setItems(r.data || []);
      setCustomers(c.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (statusFilter !== "all" && it.status !== statusFilter) return false;
      if (typeFilter !== "all" && it.check_type !== typeFilter) return false;
      if (q.trim().length >= 2) {
        if (!matchesAll(q, [it.check_no, it.customer_name, it.bank_name])) return false;
      }
      return true;
    });
  }, [items, q, statusFilter, typeFilter]);

  const totalsByStatus = useMemo(() => {
    const acc = { portfoyde: 0, bankada: 0, tahsil: 0, karsiliksiz: 0 };
    for (const it of items) {
      if (acc[it.status] !== undefined) acc[it.status] += Number(it.amount || 0);
    }
    return acc;
  }, [items]);

  const openNew = () => { setEditing(null); setOpenDialog(true); };
  const openEdit = (it) => { setEditing(it); setOpenDialog(true); };

  const remove = async (it) => {
    if (!window.confirm(`${it.check_no} silinsin mi?`)) return;
    try {
      await api.delete(`/finance/checks/${it.id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Çek & Senet Yönetimi"
        subtitle="Portföy, banka, tahsil ve karşılıksız durumları takibi"
        breadcrumbs={["Ana Sayfa", "Cari Hesap", "Çek/Senet"]}
        actions={
          <Button data-testid="add-check-btn" onClick={openNew}
            className="bg-blue-600 hover:bg-blue-500 rounded-sm" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Yeni Çek/Senet
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-5">
        {/* Totals strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="checks-totals">
          <TotalCard color="blue" label="Portföyde" value={`₺${fmt(totalsByStatus.portfoyde)}`} />
          <TotalCard color="amber" label="Bankada" value={`₺${fmt(totalsByStatus.bankada)}`} />
          <TotalCard color="emerald" label="Tahsil Edilen" value={`₺${fmt(totalsByStatus.tahsil)}`} />
          <TotalCard color="red" label="Karşılıksız" value={`₺${fmt(totalsByStatus.karsiliksiz)}`} />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="checks-search"
              placeholder="Çek no, müşteri veya banka ara…"
              className="rounded-sm h-8 pl-8 text-xs" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="rounded-sm h-8 w-32 text-xs" data-testid="filter-check-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="cek">Çek</SelectItem>
              <SelectItem value="senet">Senet</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-sm h-8 w-40 text-xs" data-testid="filter-check-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm durumlar</SelectItem>
              {Object.entries(STATUS_STYLES).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <Card className="rounded-sm bg-card border-border p-10 text-center">
            <ScrollText className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <div className="text-sm text-muted-foreground">
              {items.length === 0 ? "Henüz çek/senet kaydı yok. İlkini ekleyin." : "Filtreye uyan kayıt bulunamadı."}
            </div>
          </Card>
        ) : (
          <Card className="rounded-sm bg-card border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] uppercase tracking-widest font-mono">Tür</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest font-mono">No</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest font-mono">Müşteri</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest font-mono">Banka</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest font-mono">Vade</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Tutar</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest font-mono">Durum</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((it) => {
                  const st = STATUS_STYLES[it.status] || STATUS_STYLES.portfoyde;
                  return (
                    <TableRow key={it.id} className="border-border" data-testid={`check-row-${it.id}`}>
                      <TableCell><Badge className="rounded-sm text-[10px] uppercase font-mono bg-muted border-border">{it.check_type}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{it.check_no}</TableCell>
                      <TableCell className="text-sm">{it.customer_name}</TableCell>
                      <TableCell className="text-xs">{it.bank_name || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtDate(it.due_date)}</TableCell>
                      <TableCell className="text-right font-mono font-bold">₺{fmt(it.amount)}</TableCell>
                      <TableCell>
                        <Badge className={`rounded-sm text-[10px] uppercase tracking-wider font-mono border ${st.cls}`}>
                          {st.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(it)}
                          data-testid={`edit-check-${it.id}`}
                          className="text-muted-foreground hover:text-foreground rounded-sm h-7 px-2">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => remove(it)}
                          data-testid={`delete-check-${it.id}`}
                          className="text-muted-foreground hover:text-red-500 rounded-sm h-7 px-2">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <CheckDialog open={openDialog} onOpenChange={setOpenDialog} editing={editing}
        customers={customers} onSaved={() => { setOpenDialog(false); load(); }} />
    </div>
  );
}

function TotalCard({ color, label, value }) {
  const bg = {
    blue: "text-blue-600 dark:text-blue-400",
    amber: "text-amber-600 dark:text-amber-400",
    emerald: "text-emerald-600 dark:text-emerald-400",
    red: "text-red-600 dark:text-red-400",
  }[color];
  return (
    <Card className="rounded-sm bg-card border-border p-4">
      <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{label}</div>
      <div className={`text-xl font-bold mt-1 font-mono ${bg}`}>{value}</div>
    </Card>
  );
}

function CheckDialog({ open, onOpenChange, editing, customers, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? {
      customer_id: editing.customer_id, check_type: editing.check_type,
      check_no: editing.check_no, bank_name: editing.bank_name,
      amount: editing.amount, due_date: (editing.due_date || "").split("T")[0],
      issue_date: (editing.issue_date || "").split("T")[0],
      status: editing.status, notes: editing.notes || "",
    } : {
      customer_id: customers[0]?.id || "", check_type: "cek",
      check_no: "", bank_name: "", amount: 0,
      due_date: "", issue_date: "", status: "portfoyde", notes: "",
    });
  }, [open, editing, customers]);

  const submit = async () => {
    if (!form.customer_id) return toast.error("Müşteri seçin");
    if (!form.check_no?.trim()) return toast.error("Çek/senet no gerekli");
    if (!form.due_date) return toast.error("Vade tarihi gerekli");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Tutar 0'dan büyük olmalı");
    setSaving(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        check_type: form.check_type || "cek",
        check_no: form.check_no.trim(),
        bank_name: form.bank_name || "",
        amount: Number(form.amount),
        due_date: new Date(form.due_date).toISOString(),
        issue_date: form.issue_date ? new Date(form.issue_date).toISOString() : null,
        status: form.status || "portfoyde",
        notes: form.notes || "",
      };
      if (editing) {
        await api.patch(`/finance/checks/${editing.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post(`/finance/checks`, payload);
        toast.success("Kaydedildi");
      }
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="check-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Çek/Senet Düzenle" : "Yeni Çek/Senet"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs uppercase font-mono text-muted-foreground">Müşteri</Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
              <SelectTrigger className="rounded-sm" data-testid="check-customer-select"><SelectValue placeholder="Müşteri seçin" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-mono text-muted-foreground">Tür</Label>
            <Select value={form.check_type} onValueChange={(v) => setForm({ ...form, check_type: v })}>
              <SelectTrigger className="rounded-sm" data-testid="check-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cek">Çek</SelectItem>
                <SelectItem value="senet">Senet</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-mono text-muted-foreground">Durum</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="rounded-sm" data-testid="check-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_STYLES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-mono text-muted-foreground">Çek/Senet No</Label>
            <Input value={form.check_no || ""} onChange={(e) => setForm({ ...form, check_no: e.target.value })}
              data-testid="check-no-input" className="rounded-sm font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-mono text-muted-foreground">Banka / Keşideci</Label>
            <Input value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
              data-testid="check-bank-input" className="rounded-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-mono text-muted-foreground">Tutar (₺)</Label>
            <Input type="number" step="0.01" value={form.amount || 0}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              data-testid="check-amount-input" className="rounded-sm font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-mono text-muted-foreground">Vade</Label>
            <Input type="date" value={form.due_date || ""}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              data-testid="check-due-input" className="rounded-sm font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase font-mono text-muted-foreground">Keşide Tarihi</Label>
            <Input type="date" value={form.issue_date || ""}
              onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              className="rounded-sm font-mono" />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label className="text-xs uppercase font-mono text-muted-foreground">Notlar</Label>
            <Textarea rows={2} value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="rounded-sm resize-none" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">İptal</Button>
          <Button onClick={submit} disabled={saving} data-testid="check-save-btn"
            className="bg-blue-600 hover:bg-blue-500 rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
